import { pageFaqAdditions } from "@/data/pageFaqs";
import { PageFaqs } from "@/components/site/PageFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { m, useReducedMotion } from "framer-motion";
import { canonicalLinks, ogUrl, ogImage, jsonLd, breadcrumbSchema, faqPageSchema, webPageSchema } from "@/lib/seo";
import { EngagementLadder } from "@/components/diagrams/EngagementLadder";
import { NinetyDayArc } from "@/components/diagrams/NinetyDayArc";
import { DiagramFrame, rise } from "@/components/diagrams/DiagramFrame";
import { PhotoAside } from "@/components/site/PhotoBand";
import siteOfficeImg from "@/assets/photos/site-office-plans.webp";

const faqs = pageFaqAdditions["/how-we-work"];

export const Route = createFileRoute("/how-we-work")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "How we work | Ajility" },
      {
        name: "description",
        content:
          "The same delivery process for every Ajility engagement, from enquiry to handover. Scope and price are taken from the published catalogue.",
      },
      { property: "og:title", content: "How we work | Ajility" },
      {
        property: "og:description",
        content:
          "The same delivery process for every Ajility engagement, from enquiry to handover. Scope and price are taken from the published catalogue.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "How we work | Ajility" },
      { name: "twitter:description", content: "The same delivery process for every Ajility engagement, from enquiry to handover. Scope and price are taken from the published catalogue." },
      ...ogImage("how-we-work", "Ajility delivery process from enquiry to handover"),
      ogUrl("/how-we-work"),
    ],
    links: canonicalLinks("/how-we-work"),
    scripts: [
      jsonLd(faqPageSchema("/how-we-work", faqs)),
      jsonLd(breadcrumbSchema([{ name: "How we work", path: "/how-we-work" }])),
      jsonLd(webPageSchema({ image: "how-we-work",
        path: "/how-we-work",
        name: "How we work | Ajility",
        description: "The same delivery process for every Ajility engagement, from enquiry to handover. Scope and price are taken from the published catalogue.",
      })),
    ],
  }),
  component: HowWeWorkPage,
});

const steps = [
  "Your enquiry is answered within one business day, by a person, referencing what you actually sent.",
  "We check the fit before anything is booked. If Ajility is not the right answer, you are told so in writing, at no cost.",
  "A thirty-minute fit call. Not a pitch. It ends with a one-sentence view of what we think your constraint is, and an agreed next step with a date on it.",
  "Scope and price, taken from the published catalogue. The price on the site is the price. Where the budget is the constraint, the answer is a smaller piece of work, not a cheaper version of the same one.",
  "A written proposal stating the constraint in your words, what is in scope, what is explicitly out, the price and a decision date.",
  "An engagement letter, executed before any work begins. No exceptions.",
  "Kickoff within ten working days. The cadence is booked in calendars for the full engagement, not just the first fortnight.",
  "Delivery to a fixed rhythm. Sessions are not moved for convenience. Every action carries a named owner and a date.",
  "A written review pack, monthly on a retainer and at each milestone on an install. It states what moved, what did not and the honest reason.",
  "An explicit decision to continue, pause or close. Engagements are not allowed to fade out.",
  "Handover of everything installed, in a form you own and your team can run, with a written note on what to watch over the next two quarters.",
];

const standards = [
  "No engagement begins without a signed agreement.",
  "The catalogue price is the price. Discounting is not available.",
  "Every deliverable is named, dated and owned.",
  "No client metric or case detail is published without written consent; client names appear only with permission.",
];

function EngagementSpine({ className }: { className?: string }) {
  const reduced = useReducedMotion() ?? false;
  const stages = [
    { name: "Operating Scan", note: "free" },
    { name: "Operating Diagnostic" },
    { name: "Full Diagnostic" },
    { name: "Install" },
    { name: "Retainer" },
  ];
  const startX = 60;
  const boxW = 152;
  const boxH = 80;
  const gap = 30;
  const lineY = 148;

  return (
    <DiagramFrame
      className={className}
      caption="The delivery sequence, from the free scan to an ongoing retainer."
      captionClassName="italic"
    >
      <svg
        viewBox="0 0 960 260"
        role="img"
        aria-labelledby="spine-title spine-desc"
        className="h-auto w-full max-w-full"
      >
        <title id="spine-title">The engagement spine from free scan to retainer</title>
        <desc id="spine-desc">
          A horizontal staged flow of five steps: Operating Scan (free), Operating
          Diagnostic, Full Diagnostic, Install and Retainer. Arrows connect each step.
        </desc>

        {stages.map((s, i) => {
          const x = startX + i * (boxW + gap);
          const isLast = i === stages.length - 1;
          const nextX = x + boxW + gap;
          return (
            <m.g key={s.name} {...rise(reduced, i * 0.06)}>
              <rect
                x={x}
                y={lineY - boxH / 2}
                width={boxW}
                height={boxH}
                rx="14"
                className={isLast ? "fill-midnight stroke-midnight" : "fill-card stroke-border"}
                strokeWidth="2"
              />
              <text
                x={x + boxW / 2}
                y={lineY - 6}
                textAnchor="middle"
                fontSize="16"
                fontWeight="600"
                className={isLast ? "fill-frost" : "fill-foreground"}
              >
                {s.name}
              </text>
              {s.note ? (
                <text
                  x={x + boxW / 2}
                  y={lineY + 20}
                  textAnchor="middle"
                  fontSize="13"
                  className={isLast ? "fill-frost/80" : "fill-charcoal"}
                >
                  {s.note}
                </text>
              ) : null}
              {!isLast ? (
                <g>
                  <line
                    x1={x + boxW + 6}
                    y1={lineY}
                    x2={nextX - 14}
                    y2={lineY}
                    className="stroke-steel"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d={`M ${nextX - 18} ${lineY - 5} L ${nextX - 8} ${lineY} L ${nextX - 18} ${lineY + 5}`}
                    className="fill-none stroke-steel"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              ) : null}
            </m.g>
          );
        })}

      </svg>
    </DiagramFrame>
  );
}

function HowWeWorkPage() {
  return (
    <>
      <section className="relative overflow-hidden bg-midnight-deep pt-40 pb-24 text-frost">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <span className="eyebrow on-dark">Delivery</span>
          <h1 className="mt-6 font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl text-balance">
            How we work
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-frost/75">
            The package determines what is delivered. This process determines how every engagement is sold, started, run and closed. It is the same sequence on a three-thousand-dollar process fix as on a nine-month program, compressed where the work is small.
          </p>
        </div>
      </section>

      <section className="bg-background py-20">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <EngagementSpine />
        </div>
      </section>

      <section className="bg-background pt-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <span className="eyebrow">The pathway</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">From free to retained</h2>
          <div className="mt-10">
            <EngagementLadder />
          </div>
          <div className="mt-20">
            <span className="eyebrow">The first ninety days</span>
            <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">Diagnose, design, install, review</h2>
            <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-center">
              <NinetyDayArc />
              <PhotoAside
                src={siteOfficeImg}
                alt="Plans spread across the table in a small site office, with a hard hat and tape measure alongside"
                className="h-52 sm:h-64 lg:h-full lg:min-h-56"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background py-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <ol className="space-y-12">
            {steps.map((step, index) => (
              <m.li
                key={index}
                initial={false}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.04 }}
                className="flex gap-6"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-midnight font-sans text-sm font-semibold text-frost">
                  {index + 1}
                </span>
                <p className="mt-2 text-lg leading-relaxed text-foreground">
                  {step}
                </p>
              </m.li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-mist py-24">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <h2 className="font-serif text-3xl text-foreground sm:text-4xl">Our standards</h2>
          <p className="mt-4 max-w-2xl text-charcoal">
            These apply to every engagement, regardless of size or duration.
          </p>
          <ul className="mt-12 grid gap-6 sm:grid-cols-2">
            {standards.map((standard, index) => (
              <m.li
                key={index}
                initial={false}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="card-panel"
              >
                <p className="text-lg leading-relaxed text-foreground">{standard}</p>
              </m.li>
            ))}
          </ul>
        </div>
      </section>

      <PageFaqs faqs={faqs} />

      <section className="bg-midnight-deep py-24 text-frost">
        <div className="mx-auto max-w-5xl px-5 text-center sm:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl">Ready to see if this fits?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-frost/75">
            Send an enquiry. You will receive a written response within one business day.
          </p>
          <div className="mt-10">
            <Link
              to="/contact"
              className="inline-flex rounded-full bg-frost px-6 py-3 text-sm font-semibold text-midnight-deep hover:bg-white"
            >
              Start an enquiry
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
